/**
 * @file DockBorderTimer.tsx
 * @brief A dock's border as the minute timer: a grey track, a brand line that
 *        fills clockwise from the top centre once a minute, and the time on a
 *        small pill riding its tip. Hold freezes it in grey.
 *
 * @details
 * DES 11-10-2026 (recorder dock trial, then "make this dock our widget as
 * well"). The rule is ListeningMicButton's ring (`elapsedMs % 60s`). Shared by
 * the recorder dock (RecorderDock.tsx) and the desktop widget's pill
 * (DesktopWidgetPage.tsx), so both run the same timer. Fills its positioned
 * parent; the parent's own corner radius is passed as `radius`.
 *
 * @author DES
 * @date 11-10-2026
 */

import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { cn } from "@/shared/theme/cn";

const MINUTE_MS = 60_000;

/** Rounded-rect outline starting at the top centre, clockwise. */
function outline(w: number, h: number, radius: number, stroke: number): string {
  const i = stroke / 2;
  const x0 = i;
  const y0 = i;
  const ww = w - stroke;
  const hh = h - stroke;
  const r = Math.max(0, Math.min(radius - i, ww / 2, hh / 2));
  const cx = x0 + ww / 2;
  return [
    `M ${cx} ${y0}`,
    `H ${x0 + ww - r}`,
    `A ${r} ${r} 0 0 1 ${x0 + ww} ${y0 + r}`,
    `V ${y0 + hh - r}`,
    `A ${r} ${r} 0 0 1 ${x0 + ww - r} ${y0 + hh}`,
    `H ${x0 + r}`,
    `A ${r} ${r} 0 0 1 ${x0} ${y0 + hh - r}`,
    `V ${y0 + r}`,
    `A ${r} ${r} 0 0 1 ${x0 + r} ${y0}`,
    `H ${cx}`,
  ].join(" ");
}

export function DockBorderTimer({
  elapsedMs,
  label,
  held,
  showBadge = true,
  radius,
  stroke = 2.25,
  trackClassName = "stroke-border",
}: {
  elapsedMs: number;
  /** The time on the riding pill, e.g. "07:54". */
  label: string;
  held: boolean;
  showBadge?: boolean;
  /** The parent's corner radius in px (a pill: half its height). */
  radius: number | "pill";
  stroke?: number;
  trackClassName?: string;
}) {
  const boxRef = useRef<HTMLSpanElement>(null);
  const pathRef = useRef<SVGPathElement>(null);
  const [size, setSize] = useState({ w: 0, h: 0 });
  useLayoutEffect(() => {
    const el = boxRef.current;
    if (!el) return;
    const measure = () => setSize({ w: el.clientWidth, h: el.clientHeight });
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    measure();
    return () => ro.disconnect();
  }, []);

  const r = radius === "pill" ? size.h / 2 : radius;
  const d = size.w > 0 && size.h > 0 ? outline(size.w, size.h, r, stroke) : "";
  const [len, setLen] = useState(0);
  useLayoutEffect(() => {
    if (pathRef.current && d) setLen(pathRef.current.getTotalLength());
  }, [d]);

  const fraction = (elapsedMs % MINUTE_MS) / MINUTE_MS;
  // A new minute starts the line again: jump, never slide back round.
  const prev = useRef(fraction);
  const wrapped = fraction < prev.current;
  useEffect(() => {
    prev.current = fraction;
  }, [fraction]);
  const motion = wrapped ? "none" : undefined;

  const [tip, setTip] = useState<{ x: number; y: number } | null>(null);
  useLayoutEffect(() => {
    if (!pathRef.current || !len) return;
    const p = pathRef.current.getPointAtLength(Math.max(0.01, len * fraction));
    setTip({ x: p.x, y: p.y });
  }, [fraction, len]);

  return (
    <span ref={boxRef} aria-hidden className="pointer-events-none absolute inset-0">
      <svg className="absolute inset-0 overflow-visible" width={size.w} height={size.h}>
        <path d={d} fill="none" strokeWidth={stroke} className={trackClassName} />
        <path
          ref={pathRef}
          d={d}
          fill="none"
          strokeWidth={stroke}
          strokeLinecap="round"
          className={held ? "stroke-muted-foreground" : "stroke-brand"}
          style={{
            strokeDasharray: len || undefined,
            strokeDashoffset: len ? len * (1 - fraction) : undefined,
            transition: motion ?? "stroke-dashoffset 900ms linear",
          }}
        />
      </svg>
      {tip && showBadge ? (
        <span
          className={cn(
            "absolute z-10 -translate-x-1/2 -translate-y-1/2 rounded-full px-1.5 py-px font-mono text-[9px] font-semibold leading-[13px] tabular-nums text-white",
            held ? "bg-muted-foreground" : "bg-brand",
          )}
          style={{ left: tip.x, top: tip.y, transition: motion ?? "left 900ms linear, top 900ms linear" }}
        >
          {label}
        </span>
      ) : null}
    </span>
  );
}

export default DockBorderTimer;
